/* Training Dashboard — super-admin's per-user training overview.
 *
 * Shows every admin user as a row with: assigned primary track + progress,
 * total topics completed, quiz attempts / passed / failed, plus a tracks
 * matrix so leadership can spot stalled volunteers at a glance.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  GraduationCap, Loader2, ArrowLeft, Search, RefreshCw, Trophy, AlertTriangle,
  CheckCircle2, XCircle, BookOpen, User as UserIcon,
} from 'lucide-react';
import NotificationsBell from '../../components/admin/NotificationsBell';
import CurriculumIO from '../../components/admin/CurriculumIO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TrainingDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/training/dashboard`,
        { withCredentials: true });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load.'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = (data?.rows || []).filter((r) => {
    if (!q) return true;
    const hay = `${r.name || ''} ${r.email || ''} ${r.role || ''} ${(r.primary_track?.name || '')}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  // Headline stats
  const headline = (data?.rows || []).reduce((acc, r) => {
    acc.total_users += 1;
    acc.with_track += r.primary_track ? 1 : 0;
    acc.certified += (r.per_track || []).filter((t) => t.certified).length;
    acc.in_progress += (r.per_track || []).filter((t) => t.completed > 0 && !t.certified).length;
    acc.attempts += r.quiz_attempts || 0;
    acc.failures += r.quizzes_failed || 0;
    return acc;
  }, { total_users: 0, with_track: 0, certified: 0, in_progress: 0, attempts: 0, failures: 0 });

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="training-dashboard-page">
      <header className="bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Admin Tools</p>
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-[#BA1826]" /> Training Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <NotificationsBell />
          <Link to="/admin/help" className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5">
            <BookOpen className="w-3 h-3" /> Help Center
          </Link>
          <Link to="/admin/dashboard" className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3 h-3" /> Main dashboard
          </Link>
          <button onClick={load} className="text-xs px-2.5 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1.5"
            data-testid="training-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </header>

      <main className="p-6 space-y-4 max-w-7xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2.5 rounded inline-flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {/* Headline KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="training-kpi-strip">
          <Kpi label="Active users" value={headline.total_users} />
          <Kpi label="Assigned a track" value={headline.with_track} />
          <Kpi label="Track completions" value={headline.certified} accent="emerald" />
          <Kpi label="In progress" value={headline.in_progress} accent="amber" />
          <Kpi label="Quiz attempts" value={headline.attempts} />
          <Kpi label="Quiz failures" value={headline.failures} accent={headline.failures > 0 ? "rose" : "neutral"} />
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, role, track…"
              className="w-full pl-8 pr-2.5 py-1.5 border border-[#E5E7EB] rounded text-xs"
              data-testid="training-search" />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
        ) : (
          <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#0A0A0A] text-white">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Assigned Track</th>
                  <th className="px-3 py-2 text-right">Topics</th>
                  <th className="px-3 py-2 text-right">Quiz attempts</th>
                  <th className="px-3 py-2 text-right">Pass / Fail</th>
                  <th className="px-3 py-2 text-left">Tracks Certified</th>
                  <th className="px-3 py-2 text-left">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-[#9CA3AF]">
                    No matching users.
                  </td></tr>
                )}
                {rows.map((r) => {
                  const pct = r.completed_total
                    ? Math.round(100 * r.completed_topics / r.completed_total)
                    : 0;
                  const certifiedTracks = (r.per_track || []).filter((t) => t.certified);
                  return (
                    <tr key={r.user_id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]"
                      data-testid={`training-row-${r.user_id}`}>
                      <td className="px-3 py-2 align-top">
                        <p className="font-semibold text-[#0A0A0A] inline-flex items-center gap-1.5">
                          <UserIcon className="w-3 h-3 text-[#9CA3AF]" /> {r.name || '—'}
                        </p>
                        <p className="text-[10px] text-[#9CA3AF]">{r.email}</p>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mt-0.5">
                          {r.role}
                        </p>
                      </td>
                      <td className="px-3 py-2 align-top max-w-[12rem]">
                        {r.primary_track ? (
                          <>
                            <p className="font-semibold truncate">{r.primary_track.name}</p>
                            {r.primary_track.progress && (
                              <div className="mt-1 flex items-center gap-1.5">
                                <div className="w-24 h-1 bg-[#E5E7EB] rounded-full overflow-hidden">
                                  <div className={`h-full ${r.primary_track.progress.certified ? 'bg-emerald-500' : 'bg-[#BA1826]'}`}
                                    style={{ width: `${r.primary_track.progress.percent || 0}%` }} />
                                </div>
                                <span className="text-[10px] font-mono">
                                  {r.primary_track.progress.completed}/{r.primary_track.progress.total}
                                </span>
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-[10px] italic text-[#9CA3AF]">Not assigned</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-right tabular-nums">
                        {r.completed_topics} / {r.completed_total}
                        <p className="text-[10px] text-[#9CA3AF]">{pct}%</p>
                      </td>
                      <td className="px-3 py-2 align-top text-right tabular-nums">{r.quiz_attempts}</td>
                      <td className="px-3 py-2 align-top text-right tabular-nums">
                        <span className="text-emerald-700 inline-flex items-center gap-0.5">
                          <CheckCircle2 className="w-3 h-3" /> {r.quizzes_passed}
                        </span>
                        {' / '}
                        <span className={r.quizzes_failed > 0 ? 'text-rose-700' : 'text-[#9CA3AF]'}>
                          <XCircle className="w-3 h-3 inline" /> {r.quizzes_failed}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        {certifiedTracks.length === 0
                          ? <span className="text-[10px] italic text-[#9CA3AF]">None yet</span>
                          : <div className="flex flex-wrap gap-1">
                              {certifiedTracks.map((t) => (
                                <span key={t.id}
                                  className="text-[9px] uppercase font-bold tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                                  <Trophy className="w-2.5 h-2.5" /> {t.name}
                                </span>
                              ))}
                            </div>
                        }
                      </td>
                      <td className="px-3 py-2 align-top text-[10px] text-[#9CA3AF]">
                        {r.last_progress_at
                          ? new Date(r.last_progress_at).toLocaleDateString()
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <CurriculumIO />
      </main>
    </div>
  );
};

const Kpi = ({ label, value, accent = 'neutral' }) => {
  const colors = {
    neutral: 'bg-white border-[#E5E7EB] text-[#0A0A0A]',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
  };
  return (
    <div className={`border rounded-lg p-3 ${colors[accent]}`}>
      <p className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1 font-mono">{value}</p>
    </div>
  );
};

export default TrainingDashboard;
